/**
 * Super Admin Hooks - React Query hooks for platform management operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { superAdminService } from '../services/superadmin.service';
import { getErrorMessage } from '../services/api';
import type {
  ListTenantsParams,
  CreateTenantRequest,
  UpdateTenantRequest,
  UpdateTenantStatusRequest,
  UpdateTenantPlanRequest,
} from '../types/superadmin';
import toast from 'react-hot-toast';

// Query keys for cache management
export const superAdminKeys = {
  all: ['superAdmin'] as const,
  analytics: () => [...superAdminKeys.all, 'analytics'] as const,
  dashboard: () => [...superAdminKeys.analytics(), 'dashboard'] as const,
  trends: (period: string) => [...superAdminKeys.analytics(), 'trends', period] as const,
  tenants: () => [...superAdminKeys.all, 'tenants'] as const,
  tenantList: (params?: ListTenantsParams) => [...superAdminKeys.tenants(), 'list', params] as const,
  tenantDetails: () => [...superAdminKeys.tenants(), 'details'] as const,
  tenant: (id: string) => [...superAdminKeys.tenantDetails(), id] as const,
};

// ==================== Analytics Hooks ====================

/**
 * Get platform-wide dashboard analytics
 */
export const useDashboardAnalytics = () => {
  return useQuery({
    queryKey: superAdminKeys.dashboard(),
    queryFn: () => superAdminService.getDashboardAnalytics(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Get usage trends over time
 * @param period - Time period (e.g., '7d', '30d', '90d')
 */
export const useUsageTrends = (period: string = '30d') => {
  return useQuery({
    queryKey: superAdminKeys.trends(period),
    queryFn: () => superAdminService.getUsageTrends(period),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};

// ==================== Tenant List Hooks ====================

/**
 * List all tenants with filters and pagination
 */
export const useTenantList = (params?: ListTenantsParams, enabled: boolean = true) => {
  return useQuery({
    queryKey: superAdminKeys.tenantList(params),
    queryFn: () => superAdminService.listTenants(params),
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled,
  });
};

/**
 * Get single tenant details
 */
export const useTenant = (tenantId: string) => {
  return useQuery({
    queryKey: superAdminKeys.tenant(tenantId),
    queryFn: () => superAdminService.getTenant(tenantId),
    enabled: !!tenantId,
  });
};

// ==================== Tenant Mutation Hooks ====================

/**
 * Create new tenant with initial admin user
 */
export const useCreateTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTenantRequest) => superAdminService.createTenant(data),
    onSuccess: () => {
      // Invalidate tenant list and analytics
      queryClient.invalidateQueries({ queryKey: superAdminKeys.tenants() });
      queryClient.invalidateQueries({ queryKey: superAdminKeys.analytics() });
      toast.success('Tenant created successfully. Activation email sent to admin.');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });
};

/**
 * Update tenant details
 */
export const useUpdateTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, data }: { tenantId: string; data: UpdateTenantRequest }) =>
      superAdminService.updateTenant(tenantId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.tenants() });
      queryClient.invalidateQueries({ queryKey: superAdminKeys.tenant(variables.tenantId) });
      toast.success('Tenant updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });
};

/**
 * Delete tenant (soft delete)
 */
export const useDeleteTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenantId: string) => superAdminService.deleteTenant(tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.tenants() });
      queryClient.invalidateQueries({ queryKey: superAdminKeys.analytics() });
      toast.success('Tenant deleted successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });
};

/**
 * Update tenant status
 */
export const useUpdateTenantStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      data,
    }: {
      tenantId: string;
      data: UpdateTenantStatusRequest;
    }) => superAdminService.updateTenantStatus(tenantId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.tenants() });
      queryClient.invalidateQueries({ queryKey: superAdminKeys.tenant(variables.tenantId) });
      queryClient.invalidateQueries({ queryKey: superAdminKeys.analytics() });
      toast.success(`Tenant status updated to ${variables.data.status}`);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });
};

/**
 * Update tenant subscription plan
 */
export const useUpdateTenantPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      data,
    }: {
      tenantId: string;
      data: UpdateTenantPlanRequest;
    }) => superAdminService.updateTenantPlan(tenantId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.tenants() });
      queryClient.invalidateQueries({ queryKey: superAdminKeys.tenant(variables.tenantId) });
      queryClient.invalidateQueries({ queryKey: superAdminKeys.analytics() });
      toast.success(`Subscription plan updated to ${variables.data.plan}`);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });
};
